import React, { useMemo, useState } from "react"
// urql and the shared Inputs/Modal helpers are still JS, so we type them loosely.
import { useMutation, useQuery } from "urql"

import ConfirmManualChargeModalJs from "../../components/shared/ConfirmManualChargeModal"
import * as InputsModule from "../../components/shared/Inputs"
import * as ModalModule from "../../components/shared/Modal"
import * as RadioButtonsModule from "../../components/shared/RadioButtons"
import TypographyJs from "../../components/shared/Typography"
import { useToast } from "../../contexts/ToastContext"
import { DEFAULT_PAYMENT_METHOD_QUERY } from "../../utils/mutations"
import { formatPrice, formatShortDate } from "../../utils/utils"

// Loose wrappers — shared design-system components are still JSX with implicit `any`
// props, so we expose them as relaxed React components inside this TSX file.
type AnyProps = Record<string, unknown> & { children?: React.ReactNode }
const Input = InputsModule.Input as unknown as React.FC<AnyProps>
const Label = InputsModule.Label as unknown as React.FC<AnyProps>
const AnimatedModal = ModalModule.AnimatedModal as unknown as React.FC<AnyProps>
const RadioWithLabel = RadioButtonsModule.RadioWithLabel as unknown as React.FC<AnyProps>
const Typography = TypographyJs as unknown as React.FC<AnyProps>
const ConfirmManualChargeModal = ConfirmManualChargeModalJs as unknown as React.FC<AnyProps>

const CHARGEABLE_APPOINTMENTS_QUERY = `
  query ChargeableAppointmentsForClient($clientId: ID!) {
    currentPractice {
      id
      chargeableAppointments(clientId: $clientId, limit: 50) {
        id
        startsAt
        amountCents
        amountCurrency
        paymentStatus
        service { id name }
      }
    }
  }
`

const CHARGE_CLIENT_CARD_MUTATION = `
  mutation ChargeClientCard(
    $clientId: ID!
    $appointmentId: ID
    $amountCents: Int
    $note: String
  ) {
    chargeClientCard(
      clientId: $clientId
      appointmentId: $appointmentId
      amountCents: $amountCents
      note: $note
    ) {
      result
      errors
      manualCharge { id amountCents state }
      appointment { id paymentStatus }
    }
  }
`

type Appointment = {
  id: string
  startsAt: string
  amountCents: number
  amountCurrency?: string | null
  paymentStatus?: string | null
  service?: { id: string; name: string } | null
}

type Client = { id: string | number; name?: string | null; firstName?: string | null }

type ChargeMode = "appointment" | "custom"

type ChargeClientCardModalProps = {
  visible: boolean
  hideModal: () => void
  client: Client
  defaultCurrency?: string | null
  practiceId: string | number
  onCharged?: () => void
}

const ChargeClientCardModal: React.FC<ChargeClientCardModalProps> = ({
  visible,
  hideModal,
  client,
  defaultCurrency,
  practiceId,
  onCharged
}) => {
  const [mode, setMode] = useState<ChargeMode>("appointment")
  const [selectedAppointmentId, setSelectedAppointmentId] = useState<string | null>(null)
  const [amountInput, setAmountInput] = useState<string>("")
  const [note, setNote] = useState<string>("")
  const [submitting, setSubmitting] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)

  const { showToast } = useToast()

  const [{ data, fetching, error: queryError }, refetchAppointments] = useQuery({
    query: CHARGEABLE_APPOINTMENTS_QUERY,
    variables: { clientId: client.id },
    pause: !visible,
    requestPolicy: "cache-and-network"
  })

  const [, chargeClientCard] = useMutation(CHARGE_CLIENT_CARD_MUTATION)

  const [{ data: paymentMethodData }] = useQuery({
    query: DEFAULT_PAYMENT_METHOD_QUERY,
    variables: { clientId: client.id, practiceId },
    pause: !visible,
    requestPolicy: "cache-and-network"
  })

  const unpaidAppointments: Appointment[] = useMemo(
    () => (data?.currentPractice?.chargeableAppointments as Appointment[] | undefined) ?? [],
    [data]
  )

  const amountCents = useMemo(() => {
    const parsed = parseFloat(amountInput.replace(/[^0-9.]/g, ""))
    if (!Number.isFinite(parsed)) return 0
    return Math.round(parsed * 100)
  }, [amountInput])

  const reset = () => {
    setMode("appointment")
    setSelectedAppointmentId(null)
    setAmountInput("")
    setNote("")
    setSubmitting(false)
    setShowConfirm(false)
  }

  const close = () => {
    reset()
    hideModal()
  }

  const canSubmit =
    !submitting && ((mode === "appointment" && !!selectedAppointmentId) || (mode === "custom" && amountCents >= 50))

  const selectedAppointment = useMemo(
    () => unpaidAppointments.find((appt) => appt.id === selectedAppointmentId) ?? null,
    [unpaidAppointments, selectedAppointmentId]
  )

  const confirmAmountCents =
    mode === "appointment" && selectedAppointment ? selectedAppointment.amountCents : amountCents
  const confirmCurrency =
    mode === "appointment" && selectedAppointment?.amountCurrency
      ? selectedAppointment.amountCurrency
      : defaultCurrency || "USD"
  const confirmAmountLabel = formatPrice(confirmAmountCents, confirmCurrency)
  const confirmChargeSummary = useMemo(() => {
    if (mode === "appointment" && selectedAppointment) {
      const serviceName = selectedAppointment.service?.name || "Appointment"
      return `${serviceName} · ${formatShortDate(selectedAppointment.startsAt)}`
    }
    if (note.trim()) return `custom amount (${note.trim()})`
    return "custom amount"
  }, [mode, selectedAppointment, note])

  const cardOnFile = paymentMethodData?.defaultPaymentMethodPractice as { brand?: string; last4?: string } | undefined

  const executeCharge = () => {
    if (!canSubmit) return
    setSubmitting(true)

    const variables =
      mode === "appointment"
        ? { clientId: client.id, appointmentId: selectedAppointmentId }
        : { clientId: client.id, amountCents, note: note.trim() || null }

    chargeClientCard(variables).then((res) => {
      setSubmitting(false)
      setShowConfirm(false)
      const payload = res.data?.chargeClientCard
      if (payload?.result === "success") {
        showToast({ content: "Payment processed successfully.", type: "success" })
        onCharged?.()
        refetchAppointments({ requestPolicy: "network-only" })
        close()
      } else {
        const message =
          payload?.errors?.join(", ") ||
          res.error?.message?.replace("[GraphQL]", "") ||
          "Payment could not be processed. Please try again."
        showToast({ content: message, type: "error" })
      }
    })
  }

  const handleReviewCharge = () => {
    if (!canSubmit) return
    setShowConfirm(true)
  }

  const headerName = client.firstName || client.name || "client"

  return (
    <AnimatedModal
      visible={visible}
      header={`Charge ${headerName}'s card on file`}
      hideModal={close}
      actionButtonCopy="Continue"
      saveDisabled={!canSubmit}
      showFooter={true}
      onSave={handleReviewCharge}
      dialogClassName="max-w-xl">
      <div className="space-y-5">
        <div>
          <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:gap-6">
            <RadioWithLabel
              id="charge-mode-appointment"
              name="charge-mode"
              label="Charge an outstanding appointment"
              checked={mode === "appointment"}
              onChange={() => setMode("appointment")}
            />
            <RadioWithLabel
              id="charge-mode-custom"
              name="charge-mode"
              label="Charge a custom amount"
              checked={mode === "custom"}
              onChange={() => setMode("custom")}
            />
          </div>
        </div>

        {mode === "appointment" ? (
          <div>
            {fetching ? (
              <Typography variant="subtitle">Loading outstanding appointments…</Typography>
            ) : queryError ? (
              <Typography variant="subtitle" className="text-red">
                There was a problem loading appointments. Try again in a moment.
              </Typography>
            ) : unpaidAppointments.length === 0 ? (
              <Typography variant="subtitle">
                This client has no outstanding appointments. Switch to “custom amount” to charge them anyway.
              </Typography>
            ) : (
              <ul className="max-h-72 divide-y divide-gray overflow-y-auto rounded border border-gray">
                {unpaidAppointments.map((appt) => {
                  const isSelected = selectedAppointmentId === appt.id
                  return (
                    <li key={appt.id}>
                      <button
                        type="button"
                        className={`flex w-full items-center justify-between gap-4 px-4 py-3 text-left transition-colors ${
                          isSelected ? "bg-teal-light" : "hover:bg-gray-ultralight"
                        }`}
                        onClick={() => setSelectedAppointmentId(appt.id)}>
                        <div>
                          <div className="font-bold">{appt.service?.name || "Appointment"}</div>
                          <Typography variant="subtitle" className="mb-0">
                            {formatShortDate(appt.startsAt)}
                          </Typography>
                        </div>
                        <div className="font-bold">
                          {formatPrice(appt.amountCents, appt.amountCurrency || defaultCurrency || "USD")}
                        </div>
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <Label htmlFor="charge-amount" className="mb-1 text-base">
                Amount
              </Label>
              <Input
                id="charge-amount"
                type="number"
                min="0.50"
                step="0.01"
                placeholder="0.00"
                value={amountInput}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAmountInput(e.target.value)}
              />
              {amountInput && amountCents < 50 && (
                <Typography variant="subtitle" className="mt-1 text-red">
                  Minimum charge is {formatPrice(50, defaultCurrency || "USD")}.
                </Typography>
              )}
            </div>
            <div>
              <Label htmlFor="charge-note" className="mb-1 text-base">
                Note (optional)
              </Label>
              <Input
                id="charge-note"
                type="text"
                placeholder="e.g. No-show fee, supplements, follow-up consult"
                value={note}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNote(e.target.value)}
                maxLength={140}
              />
              <Typography variant="subtitle" className="mt-1">
                Visible to you and stored on the manual charge record.
              </Typography>
            </div>
          </div>
        )}
      </div>
      <ConfirmManualChargeModal
        visible={showConfirm}
        hideModal={() => setShowConfirm(false)}
        onConfirm={executeCharge}
        clientName={headerName}
        amountLabel={confirmAmountLabel}
        chargeSummary={confirmChargeSummary}
        cardBrand={cardOnFile?.brand}
        cardLast4={cardOnFile?.last4}
        confirmLoading={submitting}
      />
    </AnimatedModal>
  )
}

export default ChargeClientCardModal
